/**
 * Cycles page UPSI dialog handlers. Dialogs stay presentational; this mixin calls the store.
 */

import { UPSI_STAGES } from '../../../utils/constants.js';
import { isCycleClosed } from '../../../utils/helpers.js';
import {
  HTTP_BAD_REQUEST,
  HTTP_CONFLICT,
  HTTP_FORBIDDEN,
  HTTP_NOT_FOUND,
} from '../../../../../utils/http.js';

export const UPSI_DIALOG = {
  PUBLISH: 'publish',
  REVIEW: 'review',
  DETAILS: 'details',
};

export const UPSI_PUBLISH_MODE = {
  REQUEST: 'request',
  REPUBLISH: 'republish',
};

const UPSI_SERVER_ERROR_FIELDS = ['filing_reference', 'remarks'];

export function createEmptyUpsiDialog() {
  return {
    type: null,
    mode: UPSI_PUBLISH_MODE.REQUEST,
    cycle: null,
    details: null,
    loading: false,
    serverErrors: {},
  };
}

export function mapUpsiServerErrors(fieldErrors) {
  const serverErrors = {};
  (Array.isArray(fieldErrors) ? fieldErrors : []).forEach((item) => {
    if (!item || !UPSI_SERVER_ERROR_FIELDS.includes(item.field)) return;
    serverErrors[item.field] = item.message || '';
  });
  return serverErrors;
}

export function readUpsiVersion(source) {
  if (!source || typeof source !== 'object') return 0;
  const state = source.upsi_state;
  const raw = state && state.version != null ? state.version : source.upsi_version;
  if (raw == null || raw === '') return 0;
  const numeric = Number(raw);
  return Number.isNaN(numeric) ? 0 : numeric;
}

export function publishUpsiVersion({ mode, cycle, details }) {
  if (mode === UPSI_PUBLISH_MODE.REPUBLISH) return readUpsiVersion(details);
  return readUpsiVersion(cycle);
}

export function canPublishUpsiAgain(details, canPublishRight, cycle) {
  if (cycle && !isCycleClosed(cycle.stage)) return false;
  const state = (details && details.upsi_state) || {};
  const perms = (details && details.upsi_permissions) || {};
  return Number(state.stage) === UPSI_STAGES.REJECTED && !!perms.can_request && !!canPublishRight;
}

/**
 * Row patch from a successful reopen response. Uses the cycle the server returned.
 * Returns null when that payload does not include the new UPSI stage.
 *
 * @param {string} cycleId
 * @param {Object} response axios response
 * @returns {Object|null}
 */
export function upsiPatchFromReopenResponse(cycleId, response) {
  const envelope = response && response.data;
  const payload = envelope && typeof envelope === 'object' ? envelope : {};
  const rows = Array.isArray(payload.data) ? payload.data : [];
  const match = rows.find((row) => row && row._id && String(row._id) === String(cycleId))
    || rows.find((row) => row && row.upsi_state && row.upsi_state.stage != null);
  if (!match || !match.upsi_state || match.upsi_state.stage == null) return null;
  const patch = { _id: cycleId || match._id };
  if (match.stage != null) patch.stage = match.stage;
  if (match.stage_text != null) patch.stage_text = match.stage_text;
  patch.upsi_state = match.upsi_state;
  if (match.upsi_permissions) patch.upsi_permissions = match.upsi_permissions;
  return patch;
}

export function patchFromUpsiDetails(cycle, details) {
  const cycleInfo = (details && details.cycle) || {};
  const patch = { _id: (cycle && cycle._id) || cycleInfo._id };
  if (cycleInfo.stage != null) patch.stage = cycleInfo.stage;
  if (cycleInfo.stage_text != null) patch.stage_text = cycleInfo.stage_text;
  if (details && details.upsi_state) patch.upsi_state = details.upsi_state;
  if (details && details.upsi_permissions) patch.upsi_permissions = details.upsi_permissions;
  return patch;
}

function openDialog({ type, mode, cycle, details }) {
  return {
    type,
    mode: mode || UPSI_PUBLISH_MODE.REQUEST,
    cycle: cycle || null,
    details: details || null,
    loading: false,
    serverErrors: {},
  };
}

/** Latest open attempt. A slower fetch must not replace a dialog opened later. */
function claimUpsiOpen(vm) {
  vm._upsiOpenSeq = (vm._upsiOpenSeq || 0) + 1;
  return vm._upsiOpenSeq;
}

function isUpsiOpenCurrent(vm, openSeq) {
  return !vm._isDestroyed && vm._upsiOpenSeq === openSeq;
}

/** Visible dialog. Dismiss or replace invalidates in-flight writes. */
function claimUpsiDialog(vm) {
  vm._upsiDialogSeq = (vm._upsiDialogSeq || 0) + 1;
  return vm._upsiDialogSeq;
}

function isUpsiDialogCurrent(vm, dialogSeq) {
  return !vm._isDestroyed && vm._upsiDialogSeq === dialogSeq;
}

function showUpsiDialog(vm, dialog) {
  claimUpsiDialog(vm);
  vm.upsiDialog = dialog;
}

function refreshUpsiTable(vm) {
  if (vm._isDestroyed) return;
  vm.formatCycles();
  vm.applyFilters({ resetPage: false });
}

export default {
  computed: {
    upsiDialogTypes() {
      return UPSI_DIALOG;
    },
  },

  beforeDestroy() {
    claimUpsiOpen(this);
    claimUpsiDialog(this);
  },

  methods: {
    async handleUpsiPublish(cycle, mode) {
      if (!cycle || !cycle._id || !isCycleClosed(cycle.stage)) return;
      const publishMode = mode === UPSI_PUBLISH_MODE.REPUBLISH
        ? UPSI_PUBLISH_MODE.REPUBLISH
        : UPSI_PUBLISH_MODE.REQUEST;
      console.log('[Cycles]:', { action: 'upsi-publish', id: cycle._id, mode: publishMode });
      const openSeq = claimUpsiOpen(this);
      const details = await this.$store.dispatch('project/fetchUpsiDetails', cycle._id);
      if (!isUpsiOpenCurrent(this, openSeq)) return;
      if (!details && publishMode === UPSI_PUBLISH_MODE.REPUBLISH) return;
      showUpsiDialog(this, openDialog({
        type: UPSI_DIALOG.PUBLISH,
        mode: publishMode,
        cycle,
        details: details || null,
      }));
    },

    async handleUpsiReview(cycle) {
      if (!cycle || !cycle._id || !isCycleClosed(cycle.stage)) return;
      console.log('[Cycles]:', { action: 'upsi-review', id: cycle._id });
      const openSeq = claimUpsiOpen(this);
      const details = await this.$store.dispatch('project/fetchUpsiDetails', cycle._id);
      if (!details) return;
      const canReview = !!(details.upsi_permissions && details.upsi_permissions.can_review);
      if (!isUpsiOpenCurrent(this, openSeq)) {
        if (!canReview) this.syncUpsiRow(cycle, details);
        return;
      }
      if (canReview) {
        showUpsiDialog(this, openDialog({ type: UPSI_DIALOG.REVIEW, cycle, details }));
        return;
      }
      this.syncUpsiRow(cycle, details);
      showUpsiDialog(this, openDialog({ type: UPSI_DIALOG.DETAILS, cycle, details }));
    },

    async handleUpsiViewDetails(cycle) {
      if (!cycle || !cycle._id) return;
      console.log('[Cycles]:', { action: 'upsi-view-details', id: cycle._id });
      const openSeq = claimUpsiOpen(this);
      const details = await this.$store.dispatch('project/fetchUpsiDetails', cycle._id);
      if (!details || !isUpsiOpenCurrent(this, openSeq)) return;
      showUpsiDialog(this, openDialog({ type: UPSI_DIALOG.DETAILS, cycle, details }));
    },

    syncUpsiRow(cycle, details) {
      this.$store.commit('project/patchProjectUpsi', patchFromUpsiDetails(cycle, details));
      refreshUpsiTable(this);
    },

    async applyUpsiWriteResult(dialogSeq, result) {
      if (this._isDestroyed) return;
      const current = isUpsiDialogCurrent(this, dialogSeq);
      if (Array.isArray(result)) {
        if (!current) return;
        this.upsiDialog = {
          ...this.upsiDialog,
          loading: false,
          serverErrors: mapUpsiServerErrors(result),
        };
        return;
      }
      if (current) this.closeUpsiDialog();
      await this.refreshData();
    },

    async submitUpsiPublish({ filingReference, remarks, publicationAt } = {}) {
      const dialog = this.upsiDialog || createEmptyUpsiDialog();
      const cycle = dialog.cycle;
      if (!cycle || !cycle._id) return;
      const dialogSeq = this._upsiDialogSeq;
      this.upsiDialog = { ...dialog, loading: true, serverErrors: {} };
      try {
        const result = await this.$store.dispatch('project/requestUpsiPublish', {
          id: cycle._id,
          filingReference,
          remarks,
          publicationAt,
          upsiVersion: publishUpsiVersion({
            mode: dialog.mode,
            cycle,
            details: dialog.details,
          }),
        });
        await this.applyUpsiWriteResult(dialogSeq, result);
      } catch (err) {
        this.settleUpsiWriteError('submitUpsiPublish', err, dialogSeq);
      }
    },

    async submitUpsiReview({ decision, remarks } = {}) {
      const dialog = this.upsiDialog || createEmptyUpsiDialog();
      const cycle = dialog.cycle;
      const details = dialog.details;
      const state = (details && details.upsi_state) || {};
      if (!cycle || !cycle._id || !details) return;
      const dialogSeq = this._upsiDialogSeq;
      this.upsiDialog = { ...dialog, loading: true, serverErrors: {} };
      try {
        const result = await this.$store.dispatch('project/reviewUpsiRequest', {
          id: cycle._id,
          requestId: state.request_id,
          decision,
          remarks,
          upsiVersion: readUpsiVersion(details),
        });
        await this.applyUpsiWriteResult(dialogSeq, result);
      } catch (err) {
        this.settleUpsiWriteError('submitUpsiReview', err, dialogSeq);
      }
    },

    settleUpsiWriteError(action, err, dialogSeq) {
      console.error(`[Cycles]: ${action} error`, err);
      if (this._isDestroyed) return;
      const status = err && err.response ? err.response.status : 0;
      const current = isUpsiDialogCurrent(this, dialogSeq);
      const refreshesRow = status === HTTP_FORBIDDEN
        || status === HTTP_NOT_FOUND
        || status === HTTP_CONFLICT;
      if (!current) {
        if (refreshesRow) refreshUpsiTable(this);
        return;
      }
      if (status === HTTP_BAD_REQUEST) {
        const fieldErrors = err.response.data && err.response.data.data;
        this.upsiDialog = {
          ...this.upsiDialog,
          loading: false,
          serverErrors: mapUpsiServerErrors(fieldErrors),
        };
        return;
      }
      this.upsiDialog = { ...this.upsiDialog, loading: false };
      if (refreshesRow) refreshUpsiTable(this);
      if (status === HTTP_CONFLICT) this.closeUpsiDialog();
    },

    closeUpsiDialog() {
      claimUpsiOpen(this);
      claimUpsiDialog(this);
      this.upsiDialog = createEmptyUpsiDialog();
    },

    onUpsiDialogInput(type, open) {
      if (open) return;
      this.dismissUpsiDialog(type);
    },

    dismissUpsiDialog(type) {
      if (!this.upsiDialog || this.upsiDialog.type !== type) return;
      this.closeUpsiDialog();
    },

    openUpsiRepublishFromDetails() {
      const dialog = this.upsiDialog || createEmptyUpsiDialog();
      if (!dialog.cycle || !isCycleClosed(dialog.cycle.stage)) return;
      console.log('[Cycles]:', { action: 'upsi-publish-again', id: dialog.cycle._id });
      claimUpsiOpen(this);
      showUpsiDialog(this, openDialog({
        type: UPSI_DIALOG.PUBLISH,
        mode: UPSI_PUBLISH_MODE.REPUBLISH,
        cycle: dialog.cycle,
        details: dialog.details,
      }));
    },
  },
};
